import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../../utils/config";

export default function AddPayment() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  /* -------------------- core form state -------------------- */
  const todayISO = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    customer_id: "",
    amount: "",
    payment_mode: "Cash",
    reference_no: "",
    payment_date: todayISO,
  });

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  /* -------------------- customer search state -------------------- */
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);

  /* -------------------- misc state -------------------- */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* -------------------- helpers -------------------- */
  const fmtINR = (n) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(n || 0));

  const refPlaceholder = useMemo(() => {
    const pm = (form.payment_mode || "").toLowerCase();
    if (pm === "cheque") return "Cheque no. / bank ref";
    if (pm === "online") return "NEFT / IMPS / RTGS ref (optional)";
    if (pm === "upi") return "UPI txn ID (optional)";
    if (pm === "card") return "Last 4 / POS ref (optional)";
    return "Reference (optional)";
  }, [form.payment_mode]);

  const canSubmit =
    form.customer_id && Number(form.amount) > 0 && Boolean(form.payment_date);

  /* -------------------- search customers (debounced) -------------------- */
  useEffect(() => {
    if (!customerQuery.trim()) {
      setCustomerResults([]);
      setShowDropdown(false);
      return;
    }

    const t = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await axios.get(
          `${API_BASE_URL}/api/customers?q=${encodeURIComponent(
            customerQuery
          )}&limit=20`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCustomerResults(res.data?.rows || []);
        setShowDropdown(true);
      } catch (e) {
        console.error("Customer search failed:", e);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [customerQuery, token]);

  /* -------------------- amount helpers -------------------- */
  const quickAdd = (val) =>
    setForm((f) => ({
      ...f,
      amount: (Number(f.amount || 0) + val).toFixed(2),
    }));

  const setExact = (val) =>
    setForm((f) => ({ ...f, amount: val.toFixed(2) }));

  /* -------------------- submit -------------------- */
  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      await axios.post(`${API_BASE_URL}/api/payments`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert(`Payment saved: ${fmtINR(form.amount)}`);
      navigate("/payments");
    } catch (err) {
      console.error("Error adding payment:", err);
      alert("Failed to add payment");
    }
  };

  /* ============================ UI ============================ */
  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-sm">
        {/* Header */}
        <div className="border-b border-slate-100 bg-slate-50 px-4 py-3 sm:px-6">
          <h2 className="text-lg font-semibold text-slate-900">
            Add Customer Payment
          </h2>
        </div>

        {error && (
          <div className="mx-4 mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={submit} className="space-y-4 px-4 py-4 sm:px-6">
          {/* -------------------- Customer Search -------------------- */}
          <div className="relative">
            <label className="mb-1 block text-sm text-slate-600">
              Customer
            </label>

            <input
              type="text"
              placeholder="Search customer name / GST / phone…"
              value={customerQuery}
              onChange={(e) => {
                setCustomerQuery(e.target.value);
                setField("customer_id", "");
              }}
              onFocus={() =>
                customerResults.length && setShowDropdown(true)
              }
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              required
            />

            {showDropdown && (
              <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                {searching && (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    Searching…
                  </div>
                )}

                {!searching && customerResults.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500">
                    No customers found
                  </div>
                )}

                {customerResults.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setField("customer_id", c.id);
                      setCustomerQuery(c.name);
                      setShowDropdown(false);
                    }}
                    className="flex w-full flex-col px-3 py-2 text-left hover:bg-slate-50"
                  >
                    <span className="text-sm font-medium text-slate-800">
                      {c.name}
                    </span>
                    <span className="text-xs text-slate-500">
                      Outstanding: ₹{c.outstanding}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* -------------------- Amount -------------------- */}
          <div>
            <label className="mb-1 block text-sm text-slate-600">Amount</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
                ₹
              </span>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                className="w-full rounded-lg border border-slate-300 bg-white px-7 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.amount}
                onChange={(e) => setField("amount", e.target.value)}
                required
              />
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {[500, 1000, 2000].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => quickAdd(v)}
                  className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
                >
                  + {fmtINR(v).replace(".00", "")}
                </button>
              ))}
              {!!Number(form.amount) && (
                <button
                  type="button"
                  onClick={() => setExact(Number(form.amount))}
                  className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                >
                  Use exact
                </button>
              )}
            </div>
          </div>

          {/* -------------------- Mode & Reference -------------------- */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm text-slate-600">
                Payment Mode
              </label>
              <select
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.payment_mode}
                onChange={(e) => setField("payment_mode", e.target.value)}
              >
                <option>Cash</option>
                <option>Online</option>
                <option>UPI</option>
                <option>Cheque</option>
                <option>Card</option>
              </select>
            </div>

            <div>
              <label className="mb-1 block text-sm text-slate-600">
                Reference
              </label>
              <input
                type="text"
                placeholder={refPlaceholder}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.reference_no}
                onChange={(e) => setField("reference_no", e.target.value)}
              />
            </div>
          </div>

          {/* -------------------- Date -------------------- */}
          <div>
            <label className="mb-1 block text-sm text-slate-600">
              Payment Date
            </label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={form.payment_date}
                onChange={(e) =>
                  setField("payment_date", e.target.value)
                }
              />
              <button
                type="button"
                onClick={() => setField("payment_date", todayISO)}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => {
                  const d = new Date();
                  d.setDate(d.getDate() - 1);
                  setField("payment_date", d.toISOString().slice(0, 10));
                }}
                className="rounded-md border border-slate-300 px-2 py-1 text-xs"
              >
                Yesterday
              </button>
            </div>
          </div>

          {/* -------------------- Actions -------------------- */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white ${
                canSubmit
                  ? "bg-blue-600 hover:bg-blue-700"
                  : "cursor-not-allowed bg-slate-300"
              }`}
            >
              Save Payment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
